'use client';

import { useEffect, useRef, useState } from 'react';
import ParticleCanvas from '../home/components/ParticleCanvas';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function LegalClaimsPage() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => {
      if (sectionRef?.current) {
        observer?.unobserve(sectionRef?.current);
      }
    };
  }, []);

  return (
    <>
      <ParticleCanvas />
      <div className="vignette" aria-hidden="true" />
      <Header />
      <main className="relative min-h-screen pt-32 pb-20 px-6" style={{ zIndex: 2 }}>
        <div 
          ref={sectionRef}
          className={`max-w-[800px] mx-auto transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Hero Title */}
          <h1 
            className="text-5xl md:text-6xl font-black text-center mb-4"
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #c4beff 50%, #6a5cff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Legal & Copyright Claims
          </h1>

          {/* Subtitle */}
          <p className="text-center text-vanta-muted text-lg mb-16">
            Submit legal inquiries and copyright claims.
          </p>

          {/* Warning Boxes */}
          <div className="space-y-6 mb-12">
            {/* Warning Box 1: Legal Notice */}
            <div 
              className="rounded-2xl p-6 md:p-8 flex gap-4"
              style={{
                background: 'rgba(139, 0, 0, 0.15)',
                border: '2px solid #dc2626',
                backdropFilter: 'blur(10px)',
              }}
            >
              <div className="flex-shrink-0 text-3xl" aria-hidden="true">
                ⚠️
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-3">Legal Notice</h3>
                <p className="text-vanta-text leading-relaxed">
                  Submitting false, misleading, or fraudulent legal claims may result in legal liability. VantaRust reserves the right to pursue legal action and seek damages to the fullest extent permitted by law.
                </p>
              </div>
            </div>

            {/* Warning Box 2: Incomplete Claims */}
            <div 
              className="rounded-2xl p-6 md:p-8 flex gap-4"
              style={{
                background: 'rgba(139, 0, 0, 0.15)',
                border: '2px solid #dc2626',
                backdropFilter: 'blur(10px)',
              }}
            >
              <div className="flex-shrink-0 text-3xl" aria-hidden="true">
                ⚠️
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-3">Incomplete Claims</h3>
                <p className="text-vanta-text leading-relaxed">
                  Incomplete, unverifiable, or improperly formatted legal claims will not be processed. VantaRust is under no obligation to respond to invalid or informal requests.
                </p>
              </div>
            </div>
          </div>

          {/* Content Card */}
          <div 
            className="rounded-3xl p-8 md:p-12"
            style={{
              background: 'linear-gradient(135deg, rgba(106, 92, 255, 0.08) 0%, rgba(139, 127, 255, 0.05) 100%)',
              border: '2px solid rgba(106, 92, 255, 0.2)',
              backdropFilter: 'blur(30px)',
              boxShadow: '0 25px 80px rgba(106, 92, 255, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
            }}
          >
            <div className="space-y-8 text-vanta-text">
              {/* Header */}
              <section>
                <h2 className="text-3xl font-bold text-white mb-2">Legal & Copyright Claims</h2>
                <p className="text-sm text-vanta-muted">Last updated: February 22, 2026</p>
              </section>

              {/* Introduction */}
              <section>
                <p className="leading-relaxed">
                  VantaRust respects the intellectual property rights of others and expects users and third parties to do the same.
                </p>
                <p className="leading-relaxed mt-4">
                  This page explains how to submit legal notices, copyright claims, and other formal legal requests regarding VantaRust services.
                </p>
              </section>

              {/* 1. Submission of Legal Requests */}
              <section>
                <h3 className="text-xl font-bold text-white mb-3">1. Submission of Legal Requests</h3>
                <p className="leading-relaxed mb-4">
                  If you believe that content, materials, or services provided by VantaRust infringe your copyright, intellectual property rights, or other legal rights, you may submit a formal legal notice.
                </p>
                <p className="leading-relaxed mb-2">
                  All legal notices must be submitted by email to:
                </p>
                <p className="mb-4">
                  <a href="mailto:support@vantarust.com" className="text-vanta-accent hover:text-vanta-accent-light transition-colors font-semibold text-lg">
                    support@vantarust.com
                  </a>
                </p>
                <p className="leading-relaxed font-semibold">
                  Legal notices submitted through Discord, in-game chat, social media, or informal communication channels will not be accepted or processed.
                </p>
              </section>

              {/* 2. Required Information */}
              <section>
                <h3 className="text-xl font-bold text-white mb-3">2. Required Information</h3>
                <p className="leading-relaxed mb-4">
                  To ensure proper handling, your legal notice must include all of the following information:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Your full legal name</li>
                  <li>Your physical mailing address</li>
                  <li>Your email address</li>
                  <li>A clear description of the material or content in question</li>
                  <li>The exact URL, location, or identification of the allegedly infringing content</li>
                  <li>Proof of ownership or legal authority to act on behalf of the rights holder</li>
                  <li>A statement explaining the legal basis of your claim</li>
                </ul>
                <p className="leading-relaxed mt-4 font-semibold">
                  Incomplete notices may not be processed.
                </p>
              </section>

              {/* 3. Good Faith Statement */}
              <section>
                <h3 className="text-xl font-bold text-white mb-3">3. Good Faith Statement</h3>
                <p className="leading-relaxed mb-4">
                  By submitting a legal notice, you confirm that:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>You believe in good faith that the use of the material is not authorized by the rights owner, their agent, or the law</li>
                  <li>The information you provide is accurate</li>
                  <li>You are authorized to act on behalf of the rights holder, where applicable</li>
                </ul>
                <p className="leading-relaxed mt-4 font-semibold">
                  Providing false, misleading, or fraudulent claims may result in legal consequences.
                </p>
              </section>

              {/* 4. Investigation and Response */}
              <section>
                <h3 className="text-xl font-bold text-white mb-3">4. Investigation and Response</h3>
                <p className="leading-relaxed mb-4">
                  Upon receiving a valid legal notice, VantaRust may:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Review the claim</li>
                  <li>Request additional information</li>
                  <li>Remove or restrict access to the material if appropriate</li>
                  <li>Take other actions as required by applicable law</li>
                </ul>
                <p className="leading-relaxed mt-4">
                  VantaRust reserves the right to determine the appropriate response at its sole discretion.
                </p>
                <p className="leading-relaxed mt-2 font-semibold">
                  Submission of a notice does not guarantee removal of any content.
                </p>
              </section>

              {/* 5. Counter-Notification */}
              <section>
                <h3 className="text-xl font-bold text-white mb-3">5. Counter-Notification</h3>
                <p className="leading-relaxed mb-4">
                  If you believe that content was removed or restricted in error, you may submit a counter-notification.
                </p>
                <p className="leading-relaxed mb-2">
                  Counter-notifications must include:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                  <li>Your full legal name</li>
                  <li>Your contact information</li>
                  <li>Identification of the affected content</li>
                  <li>A statement explaining why you believe the action was taken in error</li>
                </ul>
                <p className="leading-relaxed mb-2">
                  Counter-notifications must also be sent to:
                </p>
                <p>
                  <a href="mailto:support@vantarust.com" className="text-vanta-accent hover:text-vanta-accent-light transition-colors font-semibold text-lg">
                    support@vantarust.com
                  </a>
                </p>
              </section>

              {/* 6. Abuse and Misuse */}
              <section>
                <h3 className="text-xl font-bold text-white mb-3">6. Abuse and Misuse</h3>
                <p className="leading-relaxed mb-4">
                  VantaRust reserves the right to ignore, reject, or take appropriate action against:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Incomplete claims</li>
                  <li>Abusive claims</li>
                  <li>Bad faith claims</li>
                  <li>Fraudulent claims</li>
                </ul>
                <p className="leading-relaxed mt-4 font-semibold">
                  VantaRust reserves all legal rights and remedies.
                </p>
              </section>

              {/* 7. Jurisdiction */}
              <section>
                <h3 className="text-xl font-bold text-white mb-3">7. Jurisdiction</h3>
                <p className="leading-relaxed mb-4">
                  This Legal & Copyright Claims Policy is governed by the laws of Switzerland.
                </p>
                <p className="leading-relaxed">
                  Any legal disputes shall be subject to the jurisdiction of the competent courts in Switzerland, unless otherwise required by applicable law.
                </p>
              </section>

              {/* 8. Contact */}
              <section>
                <h3 className="text-xl font-bold text-white mb-3">8. Contact</h3>
                <p className="leading-relaxed mb-2">
                  All legal inquiries must be sent to:
                </p>
                <p>
                  <a href="mailto:support@vantarust.com" className="text-vanta-accent hover:text-vanta-accent-light transition-colors font-semibold text-lg">
                    support@vantarust.com
                  </a>
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}